import { ModalProps } from 'bold-ui'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { useRetificacaoAtendimentoModalQuery } from 'graphql/hooks.generated'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { Fragment, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'
import { useJustificativaVerProntuarioId } from 'view/prontuario/justificativa/useJustificativaVerProntuarioId'
import { REGISTRO_TARDIO_PATH } from 'view/registro-tardio/RegistroTardioRootView'

import { JustificativaRetificacaoAtendimentoModal } from './components/JustificativaRetificacaoAtendimentoModal'
import { RemoveRegistroConfirmModal } from './components/RemoveRegistroConfirmModal'
import {
  RetificacaoAtendimentoModel,
  RetificacaoAtendimentoOpcaoEnum,
  tipoAtendimentoRetificacaoRecord,
} from './model-retificacaoAtendimento'
import { useRetificacaoStorage } from './useJustificativaRetificacaoStorage'

interface RetificacaoAtendimentoModalProps extends Omit<ModalProps, 'onSubmit'> {
  cidadaoId: ID
  dataAtendimento: Instant
  atendProfId: ID
  historicoCabecalhoId: ID
  open: boolean
  onClose(): void
  onRemoveSuccess?(): void
  nomeCidadao: string
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
  descricaoRegistrosRemovidos?: string
}

export function RetificacaoAtendimentoModal(props: Readonly<RetificacaoAtendimentoModalProps>) {
  const {
    cidadaoId,
    dataAtendimento,
    atendProfId,
    historicoCabecalhoId,
    open,
    onClose,
    onRemoveSuccess,
    tipoAtendimentoProfissional,
    descricaoRegistrosRemovidos,
  } = props
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()
  const { analytics } = useAnalytics()

  const [modalRemoveRegistroOpen, setModalRemoveRegistroOpen] = useState(false)
  const { data: { atendimentoIndividual } = {}, loading: loadingAtendimentoId } = useRetificacaoAtendimentoModalQuery({
    skip: !atendProfId,
    variables: { atendimentoProfissionalId: atendProfId },
  })
  const { id: atendimentoId, isRegistroTardio } = atendimentoIndividual?.atendimento || {}

  const { getRetificacaoStorageValue, setRetificacaoStorageValue, deleteStorageRetificacao } = useRetificacaoStorage(
    atendimentoId
  )

  const handleModalRemoveRegistroOpen = () => setModalRemoveRegistroOpen(true)

  const handleModalRemoveRegistroClose = () => setModalRemoveRegistroOpen(false)

  const handleModalRemoveRegistroSubmit = () => {
    deleteStorageRetificacao()
    handleModalRemoveRegistroClose()
    onClose()
    onRemoveSuccess?.()
  }

  const handleModalRemoveRegistroCancel = () => {
    deleteStorageRetificacao()
    handleModalRemoveRegistroClose()
  }

  const [justificativaVerProntuarioId] = useJustificativaVerProntuarioId(`${CIDADAO_PATH}/${cidadaoId}`, cidadaoId)

  const handleRetificacaoAtendimentoModalSubmit = (values: RetificacaoAtendimentoModel) => {
    const { baseRoute, title } = tipoAtendimentoRetificacaoRecord[tipoAtendimentoProfissional] ?? {}

    const isRemoverAtendimento = values.tipo === RetificacaoAtendimentoOpcaoEnum.REMOVER_REGISTRO

    if (isRemoverAtendimento) {
      setRetificacaoStorageValue({ justificativa: values.justificativa })
      analytics.logEvent('remover_registro_RET', { tipo_de_atendimento: title })
      handleModalRemoveRegistroOpen()
    } else {
      const route = isRegistroTardio ? REGISTRO_TARDIO_PATH : baseRoute
      const callbackUrl = `${match.url}${location.search}`

      setRetificacaoStorageValue({
        justificativa: values.justificativa,
        callbackUrl,
        justificativaVerProntuarioId,
        historicoCabecalhoId,
      })

      analytics.logEvent('iniciar_atnd_RET', { tipo_de_atendimento: title })
      history.push(`${route}/${atendProfId}/retificar`)
    }
  }

  return (
    <Fragment>
      <RemoveRegistroConfirmModal
        open={modalRemoveRegistroOpen}
        atendimentoProfissionalId={atendProfId}
        historicoCabecalhoId={historicoCabecalhoId}
        justificativa={getRetificacaoStorageValue().justificativa}
        onCancelar={handleModalRemoveRegistroCancel}
        onConfirmar={handleModalRemoveRegistroSubmit}
        nomeCidadao={props.nomeCidadao.titleCase()}
        tipoAtendimentoProfissional={tipoAtendimentoProfissional}
        descricaoRegistrosRemovidos={descricaoRegistrosRemovidos}
      />
      <JustificativaRetificacaoAtendimentoModal
        cidadaoId={cidadaoId}
        dataAtendimento={dataAtendimento}
        open={open}
        onCancelar={onClose}
        onSubmit={handleRetificacaoAtendimentoModalSubmit}
        loading={loadingAtendimentoId}
        tipoAtendimentoProfissional={tipoAtendimentoProfissional}
      />
    </Fragment>
  )
}
